import { off, ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import axios from "../lib/axios";
import { db } from "../lib/firebase";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { LaunchError } from "../utils/errors";
import { getPathDatabaseByRole, renewToken } from "./auth";
import {
  MISSING_PARAMETERS,
  TOKEN_EXPIRED,
  TOKEN_INVALID,
} from "../i18n/keysTranslations";
import {
  getFirstReviewerUrl,
  normalizeApprovalReviewersMap,
} from "../components/ContentApproval/approvalReviewersUtils";

const CONTENT_NOT_FOUND = "CONTENT_NOT_FOUND";

const buildContentApprovalUrl = (endpoint, token) => {
  const baseUrl = `${getCloudFunctionUrl(endpoint)}/${endpoint}`;
  if (!token) {
    return baseUrl;
  }

  return `${baseUrl}?token=${encodeURIComponent(token)}`;
};

const normalizeContentApprovalResponse = (result) => {
  return {
    ok: Boolean(result?.ok),
    ...(result?.data || {}),
    ...(result?.message ? { message: result.message } : {}),
    ...(result?.code ? { code: result.code } : {}),
  };
};

const normalizeContentApprovalContentState = (contentState = {}) => ({
  status: contentState?.status || null,
  currentDraftID: contentState?.currentDraftID || null,
  draftRevision: Number(contentState?.draftRevision) || 0,
});

const normalizeContentApprovalContentStates = (contentStates = {}) =>
  Object.entries(contentStates || {}).reduce(
    (normalizedContentStates, [contentID, contentState]) => {
      if (!contentID) {
        return normalizedContentStates;
      }

      return {
        ...normalizedContentStates,
        [String(contentID)]: normalizeContentApprovalContentState(contentState),
      };
    },
    {}
  );

const normalizeReviewerApprovalsForContent = (reviewerApprovalsMap = {}) =>
  Object.entries(reviewerApprovalsMap || {}).reduce(
    (normalizedApprovals, [reviewerID, approval]) => {
      if (!reviewerID) {
        return normalizedApprovals;
      }

      return {
        ...normalizedApprovals,
        [String(reviewerID)]: {
          status: approval?.status || "pending",
        },
      };
    },
    {}
  );

const deriveContentApprovalViewsFromContents = (contents = {}) => {
  const contentStates = {};
  const comments = [];
  const reviewerApprovalsByContent = {};

  Object.entries(contents || {}).forEach(([contentID, entry]) => {
    const normalizedContentID = String(contentID);

    if (entry?.states) {
      contentStates[normalizedContentID] = normalizeContentApprovalContentState(
        entry.states
      );
    }

    if (entry?.reviewerApprovals) {
      reviewerApprovalsByContent[normalizedContentID] =
        normalizeReviewerApprovalsForContent(entry.reviewerApprovals);
    }

    Object.entries(entry?.comments || {}).forEach(([id, comment]) => {
      comments.push({
        id,
        contentID: normalizedContentID,
        ...comment,
      });
    });
  });

  return { contentStates, comments, reviewerApprovalsByContent };
};

const normalizeContentApprovalPayload = (
  payload = {},
  approvalContentStates = {}
) => {
  const contentID = payload?.contentID || null;
  const contentState = contentID
    ? approvalContentStates[String(contentID)] || {}
    : {};

  return {
    ...payload,
    contentID,
    status: payload?.status || contentState?.status || null,
    currentDraftID:
      payload?.currentDraftID || contentState?.currentDraftID || null,
    draftRevision:
      Number(payload?.draftRevision ?? contentState?.draftRevision) || 0,
  };
};

const getContentApprovalHandledError = (error) => {
  const responseData = error?.response?.data;
  const code = responseData?.code || error?.code;

  if (
    [
      CONTENT_NOT_FOUND,
      MISSING_PARAMETERS,
      TOKEN_EXPIRED,
      TOKEN_INVALID,
      "approvalClosed",
      "Unauthorized",
      "CONTENT_ALREADY_IN_APPROVAL",
    ].includes(code)
  ) {
    return {
      ok: false,
      code,
      message: responseData?.message || error?.message || "",
    };
  }

  return null;
};

const runContentApprovalBearerRequest = async ({
  dispatch,
  endpoint,
  data,
  functionName,
  showAlert = true,
}) => {
  try {
    const token = await dispatch(renewToken());

    if (!token) {
      return {
        ok: false,
        code: "missingAuthToken",
        message: "Missing auth token",
      };
    }

    const { data: result } = await axios({
      method: "post",
      url: buildContentApprovalUrl(endpoint),
      data,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return normalizeContentApprovalResponse(result);
  } catch (error) {
    const handledError = getContentApprovalHandledError(error);
    if (handledError) {
      return handledError;
    }

    const errorFormatted = dispatch(
      LaunchError(error, showAlert, [], {
        functionName,
      })
    );

    return {
      ok: false,
      code: errorFormatted?.code,
      message: errorFormatted?.message || error?.message || "",
    };
  }
};

export const startGetContentApprovalPayload = (token) => async (dispatch) => {
  try {
    const { data: result } = await axios({
      method: "get",
      url: buildContentApprovalUrl("getContentApprovalPayload", token),
    });

    const normalized = normalizeContentApprovalResponse(result);
    if (!normalized?.ok) {
      return normalized;
    }

    const approval = normalizeContentApprovalState({
      ...(normalized.approval || {}),
      reviewerApprovalsByContent:
        normalized.reviewerApprovalsByContent ||
        normalized.approval?.reviewerApprovalsByContent ||
        {},
    });
    const payloads = Array.isArray(normalized.payloads)
      ? normalized.payloads.map((payload) =>
          normalizeContentApprovalPayload(
            payload,
            approval?.contentStates || {}
          )
        )
      : [];

    return {
      ...normalized,
      approval,
      payloads,
      comments: approval?.comments || [],
    };
  } catch (error) {
    const handledError = getContentApprovalHandledError(error);
    if (handledError) {
      return handledError;
    }

    const errorFormatted = dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetContentApprovalPayload",
      })
    );

    return {
      ok: false,
      code: errorFormatted?.code,
      message: errorFormatted?.message || error?.message || "",
    };
  }
};

export const startSubmitContentApprovalComment =
  ({ token, body, contentID }) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: buildContentApprovalUrl("submitContentApprovalComment", token),
        data: { body, contentID },
      });

      return normalizeContentApprovalResponse(result);
    } catch (error) {
      const handledError = getContentApprovalHandledError(error);
      if (handledError) {
        return handledError;
      }

      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startSubmitContentApprovalComment",
        })
      );

      return {
        ok: false,
        code: errorFormatted?.code,
        message: errorFormatted?.message || error?.message || "",
      };
    }
  };

export const startDecideContentApproval =
  ({ token, contentID, decision = "approved", body = "" }) =>
  async (dispatch) => {
    try {
      const { data: result } = await axios({
        method: "post",
        url: buildContentApprovalUrl("decideContentApproval", token),
        data: { contentID, decision, body },
      });

      return normalizeContentApprovalResponse(result);
    } catch (error) {
      const handledError = getContentApprovalHandledError(error);
      if (handledError) {
        return handledError;
      }

      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startDecideContentApproval",
        })
      );

      return {
        ok: false,
        code: errorFormatted?.code,
        message: errorFormatted?.message || error?.message || "",
      };
    }
  };

export const startEnableContentApproval =
  ({ contentIDs }) =>
  async (dispatch) => {
    return runContentApprovalBearerRequest({
      dispatch,
      endpoint: "enableContentApproval",
      data: { contentIDs },
      functionName: "startEnableContentApproval",
      showAlert: true,
    });
  };

export const startBumpContentApprovalDraft =
  ({ contentID }) =>
  async (dispatch) => {
    return runContentApprovalBearerRequest({
      dispatch,
      endpoint: "bumpContentApprovalDraft",
      data: { contentID },
      functionName: "startBumpContentApprovalDraft",
      showAlert: true,
    });
  };

export const normalizeContentApprovalState = (approvalData) => {
  if (!approvalData) {
    return {
      enabled: false,
      status: null,
      currentDraftID: null,
      draftRevision: 0,
      url: null,
      contentIDs: [],
      contentStates: {},
      reviewerApprovalsByContent: {},
      approvalID: null,
      reviewers: [],
      comments: [],
    };
  }

  const derivedFromContents = deriveContentApprovalViewsFromContents(
    approvalData?.contents || {}
  );
  const hasDerivedStates =
    Object.keys(derivedFromContents.contentStates).length > 0;
  const normalizedContentStates = hasDerivedStates
    ? derivedFromContents.contentStates
    : Object.keys(approvalData?.contentStates || {}).length > 0
      ? normalizeContentApprovalContentStates(approvalData.contentStates)
      : {};
  const contentIDs = Object.keys(normalizedContentStates)
    .map(String)
    .filter(Boolean);
  const reviewers = normalizeApprovalReviewersMap(approvalData?.reviewers);
  const reviewerApprovalsByContent = {
    ...(approvalData?.reviewerApprovalsByContent || {}),
    ...(derivedFromContents.reviewerApprovalsByContent || {}),
  };

  return {
    ...approvalData,
    enabled: Boolean(approvalData?.enabled),
    currentDraftID: approvalData?.currentDraftID || null,
    draftRevision: Number(approvalData?.draftRevision) || 0,
    status: approvalData?.status || null,
    url: getFirstReviewerUrl({
      reviewers: approvalData?.reviewers,
      fallbackUrl: approvalData?.url || null,
    }),
    contentIDs,
    contentStates: normalizedContentStates,
    reviewerApprovalsByContent,
    approvalID: approvalData?.approvalID || null,
    reviewers,
    comments: hasDerivedStates ? derivedFromContents.comments : [],
  };
};

export const buildContentApprovalDataFromItem = (item = {}) => {
  const approvalID = item?.contentApprovalID || null;
  const status = item?.approvalStatus ?? null;

  return {
    enabled: Boolean(approvalID && status),
    status,
    url: null,
    currentDraftID: item?.currentDraftID ?? null,
    draftRevision: Number(item?.draftRevision) || 0,
    approvalID,
    contentIDs: item?.id ? [String(item.id)] : [],
    contentStates: {},
    contentID: item?.id || null,
  };
};

export const startListenerContentApproval =
  ({
    approvalID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID } = dispatch(getPathDatabaseByRole());

      if (!userID || !approvalID) {
        onUpdatedDataCallback(normalizeContentApprovalState(null));
        return false;
      }

      const dbRef = ref(db, `contentApprovals/${userID}/${approvalID}`);
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(
          normalizeContentApprovalState({
            ...snapshot.val(),
            approvalID,
          })
        );
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerContentApproval",
          context: { approvalID },
          tags: { action: "listenerContentApproval" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerContentApproval = (listenerRef) => async () => {
  if (!listenerRef) {
    return;
  }

  await off(listenerRef);
};
